import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Archive, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ContractStatusBadge } from "./ContractStatusBadge";
import { NewContractDialog } from "./NewContractDialog";
import { typeLabel, fmtDate, fmtMoney } from "@/lib/contracts";
import api from "@/lib/api";
import { toast } from "sonner";
import { translateFr as tfr } from "@/i18n";

export const WorkspaceHeader = ({ contract, onChanged }) => {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const archive = async () => {
    try {
      await api.delete(`/contracts/${contract.id}`);
      toast.success(tfr("Contrat archivé"));
      onChanged?.();
    } catch { toast.error(tfr("Impossible d'archiver le contrat")); }
  };

  const amount = fmtMoney(contract.amount_cents, contract.currency);
  const meta = [
    typeLabel(contract.contract_type),
    contract.player_name,
    contract.counterparty,
    contract.start_date || contract.end_date ? `${fmtDate(contract.start_date)} → ${fmtDate(contract.end_date)}` : null,
    amount,
  ].filter(Boolean);

  return (
    <header className="space-y-3" data-testid="contract-workspace-header">
      <button onClick={() => navigate("/contracts")} data-testid="contract-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" /> {tfr("Contrats")}
      </button>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.02em] truncate" data-testid="contract-title">{contract.title}</h1>
            <ContractStatusBadge status={contract.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid="contract-meta">
            {meta.map((m, i) => (
              <React.Fragment key={m}>
                {i > 0 && <span className="opacity-40">·</span>}
                <span>{m}</span>
              </React.Fragment>
            ))}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-muted-foreground mr-1">
            <ShieldCheck className="h-3.5 w-3.5 text-green-500" /> {tfr("Journal d'audit actif")}
          </span>
          <Button variant="outline" size="sm" data-testid="contract-edit-btn" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> {tfr("Modifier")}
          </Button>
          {contract.status !== "archived" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="contract-archive-btn" className="text-muted-foreground">
                  <Archive className="h-3.5 w-3.5 mr-1.5" /> Archiver
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{tfr("Archiver ce contrat ?")}</AlertDialogTitle>
                  <AlertDialogDescription>{tfr("Le contrat restera consultable, ses documents et son historique sont conservés de façon immuable.")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{tfr("Annuler")}</AlertDialogCancel>
                  <AlertDialogAction data-testid="contract-archive-confirm" onClick={archive}>Archiver</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      <NewContractDialog open={editOpen} onOpenChange={setEditOpen} contract={contract} onSaved={onChanged} />
    </header>
  );
};
